import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, Check, ChevronDown, ChevronUp, Edit01, Select, TextInput, XClose } from "@opendate-app/setlist";

import { useCustomCampaignShowContext } from "../CustomCampaignShowContainer";
import NodeErrorsContainer from "../NodeErrorContainer";

const ASSIGNED_STAFF = "Contact's assigned staff member";
const ASSIGNED_STAFF_LABEL = "Contact's relationship manager";

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  backgroundColor: "var(--sl-bg-primary)",
  borderBottom: "1px solid var(--sl-border-secondary)",
  padding: 12,
  cursor: "pointer",
};

const staticInfoStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 12,
  alignItems: "center",
  minWidth: 0,
};

const dropdownStyle = {
  display: "flex",
  flexDirection: "column",
  backgroundColor: "var(--sl-bg-primary)",
};

const dropdownRowStyle = {
  display: "flex",
  borderBottom: "1px solid var(--sl-border-secondary)",
  padding: "4px 12px",
  gap: 8,
  alignItems: "center",
  minHeight: 40,
};

const labelStyle = {
  fontSize: 12,
  fontWeight: 700,
  color: "var(--sl-text-quaternary)",
};

const rowLabelStyle = {
  ...labelStyle,
  fontWeight: 600,
};

const textStyle = {
  fontSize: 12,
  color: "var(--sl-text-quaternary)",
  overflowWrap: "anywhere",
};

const editFormStyle = {
  display: "flex",
  flex: 1,
  alignItems: "center",
  gap: 8,
  minWidth: 0,
};

const inputStyle = {
  flex: "1 1 240px",
  maxWidth: 350,
};

const EditableValue = ({ noun, value, setValue, isEditing, setIsEditing, onSave, onCancel }) => (
  isEditing ? (
    <form
      style={editFormStyle}
      onSubmit={(e) => {
        e.preventDefault();
        onSave(() => setIsEditing(false));
      }}
    >
      <div style={inputStyle}>
        <TextInput size="sm" autoFocus aria-label={noun} value={value || ""} onChange={(e) => setValue(e.target.value)} />
      </div>
      <Button type="submit" variant="tertiary" size="xs" icon={Check} aria-label={`Save ${noun}`} />
      <Button variant="tertiary" size="xs" icon={XClose} aria-label={`Cancel editing ${noun}`} onClick={() => { onCancel(); setIsEditing(false); }} />
    </form>
  ) : (
    <>
      <div style={textStyle}>{value}</div>
      <Button variant="tertiary" size="xs" icon={Edit01} aria-label={`Edit ${noun}`} onClick={() => setIsEditing(true)} />
    </>
  )
);

const ContentTabHeader = ({
  updateWorkflowNodeData,
  updateBeefreeTemplate,
  subject,
  setSubject,
  preheader,
  setPreheader,
}) => {
  const {
    selectedNode,
    sendDomains,
    selectedNodeFormState,
  } = useCustomCampaignShowContext();

  const [isExpanded, setIsExpanded] = useState(false);
  const isAssignedStaff = selectedNodeFormState?.node_data?.recipient_type === ASSIGNED_STAFF;
  const [to, setTo] = useState(selectedNodeFormState?.node_data?.to || "");
  const [editing, setEditing] = useState({});
  const editingProps = (field) => ({
    isEditing: !!editing[field],
    setIsEditing: (value) => setEditing((current) => ({ ...current, [field]: value })),
  });
  const selectedSendDomainId = selectedNode?.beefree_template?.email_send_domain?.send_domain_id;

  useEffect(() => {
    setTo(selectedNodeFormState?.node_data?.to || "");
  }, [selectedNodeFormState?.node_data?.to])

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={headerStyle} onClick={() => setIsExpanded(!isExpanded)}>
        <div style={staticInfoStyle}>
          <NodeErrorsContainer errors={selectedNode.workflow_errors.email_send_domain} />
          <div style={labelStyle}>From</div>
          <div style={textStyle}>{selectedNode.beefree_template.email_send_domain?.name_with_address}</div>
          <NodeErrorsContainer errors={selectedNode.workflow_errors.to} />
          <div style={labelStyle}>To</div>
          <div style={textStyle}>{isAssignedStaff ? ASSIGNED_STAFF_LABEL : selectedNodeFormState?.node_data?.to}</div>
          <NodeErrorsContainer errors={selectedNode.workflow_errors.subject} />
          <div style={labelStyle}>Subject</div>
          <div style={textStyle}>{selectedNodeFormState?.node_data?.subject}</div>
          {selectedNode.node_data.preheader ? (
            <>
              <NodeErrorsContainer errors={selectedNode.workflow_errors.preheader} />
              <div style={labelStyle}>Preheader</div>
              <div style={textStyle}>{selectedNode.node_data.preheader}</div>
            </>
          ) : null}
        </div>
        <Button
          variant="tertiary"
          size="xs"
          icon={isExpanded ? ChevronUp : ChevronDown}
          aria-label={isExpanded ? "Hide email details" : "Show email details"}
          aria-expanded={isExpanded}
        />
      </div>
      {isExpanded ? (
        <div style={dropdownStyle}>
          <div style={dropdownRowStyle}>
            <NodeErrorsContainer errors={selectedNode.workflow_errors.to} />
            <div style={rowLabelStyle}>TO:</div>
            {isAssignedStaff ? (
              <div style={textStyle}>{ASSIGNED_STAFF_LABEL}</div>
            ) : (
              <EditableValue
                noun="recipient"
                {...editingProps("to")}
                value={to}
                setValue={setTo}
                onSave={(done) => updateWorkflowNodeData({ to: to }, done)}
                onCancel={() => setTo(selectedNode.node_data?.to)}
              />
            )}
          </div>
          <div style={dropdownRowStyle}>
            <NodeErrorsContainer errors={selectedNode.workflow_errors.email_send_domain} />
            <div style={rowLabelStyle}>FROM:</div>
            <div style={inputStyle}>
              <Select
                size="sm"
                aria-label="Send domain"
                placeholder="Select a send domain"
                options={sendDomains.map((sendDomain) => ({ value: sendDomain.id, label: sendDomain.name_with_address }))}
                value={selectedSendDomainId ?? null}
                onChange={(sendDomainId) => {
                  if (sendDomainId === selectedSendDomainId) return;

                  updateBeefreeTemplate({
                    id: selectedNode?.beefree_template?.id,
                    email_send_domain_attributes: {
                      id: selectedNode?.beefree_template?.email_send_domain?.id,
                      send_domain_id: sendDomainId,
                    },
                  });
                }}
              />
            </div>
          </div>
          <div style={dropdownRowStyle}>
            <NodeErrorsContainer errors={selectedNode.workflow_errors.subject} />
            <div style={rowLabelStyle}>SUBJECT:</div>
            <EditableValue
              noun="subject"
              {...editingProps("subject")}
              value={subject}
              setValue={setSubject}
              onSave={(done) => updateWorkflowNodeData({ subject: subject }, done)}
              onCancel={() => setSubject(selectedNode.node_data?.subject)}
            />
          </div>
          <div style={dropdownRowStyle}>
            <NodeErrorsContainer errors={selectedNode.workflow_errors.preheader} />
            <div style={rowLabelStyle}>PREHEADER:</div>
            <EditableValue
              noun="preheader"
              {...editingProps("preheader")}
              value={preheader}
              setValue={setPreheader}
              onSave={(done) => updateWorkflowNodeData({ preheader: preheader }, done)}
              onCancel={() => setPreheader(selectedNode.node_data?.preheader)}
            />
          </div>
        </div>
      ) : null}
    </div>
  )
};

ContentTabHeader.propTypes = {
  updateWorkflowNodeData: PropTypes.func.isRequired,
  updateBeefreeTemplate: PropTypes.func.isRequired,
  subject: PropTypes.string,
  setSubject: PropTypes.func.isRequired,
  preheader: PropTypes.string,
  setPreheader: PropTypes.func.isRequired,
};

ContentTabHeader.defaultProps = {
  subject: null,
  preheader: null,
};

export default ContentTabHeader;
